import { v4 as uuidv4 } from "uuid";
import { FormDefinition } from "../types/formTypes";
import { FormRule } from "../types/ruleTypes";
import { JobDocumentTypeEnum } from "../../job-page/job-document/job-document.types";

export interface FormTemplate {
	title: string;
	description: string;
	bg_color: string;
	definition: FormDefinition;
	rules: FormRule[];
}

const TEMPLATES: FormTemplate[] = [
	{
		title: "Neuer Arbeitsauftrag",
		description:
			"Allgemeines Formular für Arbeitsaufträge, Kostenvoranschläge und Voranmeldungen.",
		bg_color: "green-50",
		definition: {
			sections: [
				{
					id: "sec-std-patient",
					title: "Patientendaten",
					fields: [
						{
							id: "fld-std-code",
							type: "text",
							label: "Patienten-Code",
							required: false,
						},
						{
							id: "fld-std-fname",
							type: "text",
							label: "Vorname",
							required: true,
						},
						{
							id: "fld-std-lname",
							type: "text",
							label: "Nachname",
							required: true,
						},
						{
							id: "fld-std-birthday",
							type: "date",
							label: "Geburtsdatum",
							required: false,
						},
					],
				},
				{
					id: "sec-std-details",
					title: "Auftragsdetails",
					fields: [
						{
							id: "fld-std-request-type",
							type: "radio",
							label: "Art der Anfrage",
							required: true,
							options: [
								"Arbeitsauftrag",
								"Kostenvoranschlag",
								"Voranmeldung",
							],
						},
						{
							id: "fld-std-desc",
							type: "textarea",
							label: "Beschreibung der Arbeit",
							required: true,
						},
						{
							id: "fld-std-odonto",
							type: "odontogram",
							label: "Zahnschema",
							required: false,
							odontogramFields: [
								{
									id: "fld-std-versorgung",
									type: "select",
									label: "Versorgungsart",
									required: false,
									options: [
										"Krone",
										"Teilkrone",
										"Teleskopkrone",
										"Brückenglied",
										"Veneer",
										"Inlay",
										"Onlay",
										"Overlay",
										"Implantat",
										"Implantatkrone",
										"Stiftaufbau",
										"Verblockt",
										"Geschiebe",
										"Steg",
										"Provisorium",
										"Fehlt",
									],
								},
								{
									id: "fld-std-tooth-note",
									type: "text",
									label: "Bemerkung",
									required: false,
								},
							],
						},
						{
							id: "fld-std-files",
							type: "file",
							label: "Dateien",
							required: false,
						},
					],
				},
				{
					id: "sec-std-dates",
					title: "Termine",
					fields: [
						{
							id: "fld-std-description",
							type: "title",
							label: "Bitte ausfüllen, wenn es sich um eine Voranmeldung oder einen Arbeitsauftrag handelt.",
							required: false,
						},
						{
							id: "fld-std-pickup",
							type: "date",
							label: "Tag der Abholung",
							required: false,
						},
						{
							id: "fld-std-delivery",
							type: "date",
							label: "Lieferdatum",
							required: false,
						},
					],
				},
			],
		},
		rules: [
			{
				id: "rule-std-patient",
				name: "Patient erstellen",
				enabled: true,
				conditionGroup: {
					type: "and",
					conditions: [
						{
							fieldId: "fld-std-code",
							operator: "is_not_empty",
						},
						{
							fieldId: "fld-std-fname",
							operator: "is_not_empty",
						},
						{
							fieldId: "fld-std-lname",
							operator: "is_not_empty",
						},
					],
				},
				action: {
					type: "create_patient",
					matchFields: [
						"patients.last_name",
						"patients.first_name",
						"patients.code",
					],
					fieldMappings: [
						{
							patientFieldKey: "patients.code",
							valueSource: "form_field",
							formFieldId: "fld-std-code",
						},
						{
							patientFieldKey: "patients.first_name",
							valueSource: "form_field",
							formFieldId: "fld-std-fname",
						},
						{
							patientFieldKey: "patients.last_name",
							valueSource: "form_field",
							formFieldId: "fld-std-lname",
						},
						{
							patientFieldKey: "patients.birth_date",
							valueSource: "form_field",
							formFieldId: "fld-std-birthday",
						},
					],
				},
			},
			{
				id: "rule-std-desc",
				name: "Beschreibung übernehmen",
				enabled: true,
				conditionGroup: {
					type: "and",
					conditions: [
						{
							fieldId: "fld-std-desc",
							operator: "is_not_empty",
						},
					],
				},
				action: {
					type: "prefill_job_fields",
					fieldMappings: [
						{
							jobFieldKey: "jobs.description",
							valueSource: "form_field",
							formFieldId: "fld-std-desc",
						},
					],
				},
			},
			{
				id: "rule-std-pickup",
				name: "Eingangsdatum übernehmen",
				enabled: true,
				conditionGroup: {
					type: "and",
					conditions: [
						{
							fieldId: "fld-std-pickup",
							operator: "is_not_empty",
						},
					],
				},
				action: {
					type: "prefill_job_fields",
					fieldMappings: [
						{
							jobFieldKey: "jobs.incoming_order_date",
							valueSource: "form_field",
							formFieldId: "fld-std-pickup",
						},
					],
				},
			},
			{
				id: "rule-std-delivery",
				name: "Ausgangsdatum übernehmen",
				enabled: true,
				conditionGroup: {
					type: "and",
					conditions: [
						{
							fieldId: "fld-std-delivery",
							operator: "is_not_empty",
						},
					],
				},
				action: {
					type: "prefill_job_fields",
					fieldMappings: [
						{
							jobFieldKey: "jobs.outgoing_order_date",
							valueSource: "form_field",
							formFieldId: "fld-std-delivery",
						},
					],
				},
			},
			{
				id: "rule-std-doc-dn",
				name: "Lieferschein erstellen",
				enabled: true,
				conditionGroup: {
					type: "and",
					conditions: [
						{
							fieldId: "fld-std-request-type",
							operator: "equals",
							value: "Arbeitsauftrag",
						},
					],
				},
				action: {
					type: "create_job_document",
					documentType: JobDocumentTypeEnum.DeliveryNote,
					templateId: null,
				},
			},
			{
				id: "rule-std-doc-qt",
				name: "Kostenvoranschlag erstellen",
				enabled: true,
				conditionGroup: {
					type: "and",
					conditions: [
						{
							fieldId: "fld-std-request-type",
							operator: "equals",
							value: "Kostenvoranschlag",
						},
					],
				},
				action: {
					type: "create_job_document",
					documentType: JobDocumentTypeEnum.Quotation,
					templateId: null,
				},
			},
		],
	},
	{
		title: "Reparatur",
		description:
			"Formular für Reparaturaufträge mit Schadensbeschreibung und Dringlichkeit.",
		bg_color: "amber-50",
		definition: {
			sections: [
				{
					id: "sec-rep-patient",
					title: "Patientendaten",
					fields: [
						{
							id: "fld-rep-code",
							type: "text",
							label: "Patienten-Code",
							required: false,
						},
						{
							id: "fld-rep-fname",
							type: "text",
							label: "Vorname",
							required: true,
						},
						{
							id: "fld-rep-lname",
							type: "text",
							label: "Nachname",
							required: true,
						},
						{
							id: "fld-rep-birthday",
							type: "date",
							label: "Geburtsdatum",
							required: false,
						},
					],
				},
				{
					id: "sec-rep-details",
					title: "Reparaturdetails",
					fields: [
						{
							id: "fld-rep-work-type",
							type: "select",
							label: "Art der Arbeit",
							required: true,
							options: [
								"Prothese",
								"Krone",
								"Brücke",
								"Schiene",
								"Retainer",
								"Sonstiges",
							],
						},
						{
							id: "fld-rep-damage-type",
							type: "checkbox",
							label: "Art des Schadens",
							required: true,
							options: [
								"Bruch",
								"Zahn abgebrochen",
								"Unterfütterung",
								"Klammer defekt",
								"Erweiterung nötig",
								"Verfärbung",
								"Sonstiges",
							],
						},
						{
							id: "fld-rep-desc",
							type: "textarea",
							label: "Beschreibung des Schadens",
							required: false,
						},
						{
							id: "fld-rep-urgency",
							type: "radio",
							label: "Dringlichkeit",
							required: false,
							options: ["Normal", "Eilig", "Notfall"],
						},
						{
							id: "fld-rep-odonto",
							type: "odontogram",
							label: "Zahnschema",
							required: false,
						},
						{
							id: "fld-rep-files",
							type: "file",
							label: "Fotos / Dateien",
							required: false,
						},
					],
				},
				{
					id: "sec-rep-dates",
					title: "Termine",
					fields: [
						{
							id: "fld-rep-pickup",
							type: "date",
							label: "Tag der Abholung",
							required: false,
						},
						{
							id: "fld-rep-delivery",
							type: "date",
							label: "Lieferdatum",
							required: false,
						},
					],
				},
			],
		},
		rules: [
			{
				id: "rule-rep-patient",
				name: "Patient erstellen",
				enabled: true,
				conditionGroup: {
					type: "and",
					conditions: [
						{
							fieldId: "fld-rep-code",
							operator: "is_not_empty",
						},
						{
							fieldId: "fld-rep-fname",
							operator: "is_not_empty",
						},
						{
							fieldId: "fld-rep-lname",
							operator: "is_not_empty",
						},
					],
				},
				action: {
					type: "create_patient",
					matchFields: [
						"patients.last_name",
						"patients.first_name",
						"patients.code",
					],
					fieldMappings: [
						{
							patientFieldKey: "patients.code",
							valueSource: "form_field",
							formFieldId: "fld-rep-code",
						},
						{
							patientFieldKey: "patients.first_name",
							valueSource: "form_field",
							formFieldId: "fld-rep-fname",
						},
						{
							patientFieldKey: "patients.last_name",
							valueSource: "form_field",
							formFieldId: "fld-rep-lname",
						},
						{
							patientFieldKey: "patients.birth_date",
							valueSource: "form_field",
							formFieldId: "fld-rep-birthday",
						},
					],
				},
			},
			{
				id: "rule-rep-desc",
				name: "Beschreibung übernehmen",
				enabled: true,
				conditionGroup: {
					type: "and",
					conditions: [
						{
							fieldId: "fld-rep-desc",
							operator: "is_not_empty",
						},
					],
				},
				action: {
					type: "prefill_job_fields",
					fieldMappings: [
						{
							jobFieldKey: "jobs.description",
							valueSource: "form_field",
							formFieldId: "fld-rep-desc",
						},
					],
				},
			},
			{
				id: "rule-rep-pickup",
				name: "Eingangsdatum übernehmen",
				enabled: true,
				conditionGroup: {
					type: "and",
					conditions: [
						{
							fieldId: "fld-rep-pickup",
							operator: "is_not_empty",
						},
					],
				},
				action: {
					type: "prefill_job_fields",
					fieldMappings: [
						{
							jobFieldKey: "jobs.incoming_order_date",
							valueSource: "form_field",
							formFieldId: "fld-rep-pickup",
						},
					],
				},
			},
			{
				id: "rule-rep-delivery",
				name: "Ausgangsdatum übernehmen",
				enabled: true,
				conditionGroup: {
					type: "and",
					conditions: [
						{
							fieldId: "fld-rep-delivery",
							operator: "is_not_empty",
						},
					],
				},
				action: {
					type: "prefill_job_fields",
					fieldMappings: [
						{
							jobFieldKey: "jobs.outgoing_order_date",
							valueSource: "form_field",
							formFieldId: "fld-rep-delivery",
						},
					],
				},
			},
		],
	},
];

export function getFormTemplates(): FormTemplate[] {
	return TEMPLATES;
}

export function cloneFormTemplate(
	template: FormTemplate,
	organizationId: string
): {
	title: string;
	description: string;
	bg_color: string;
	organization_id: string;
	definition: FormDefinition;
	rules: FormRule[];
} {
	const idMap = new Map<string, string>();

	const getNewId = (oldId: string): string => {
		if (!idMap.has(oldId)) {
			idMap.set(oldId, uuidv4());
		}
		return idMap.get(oldId)!;
	};

	const clonedDefinition: FormDefinition = {
		sections: template.definition.sections.map((section) => ({
			...section,
			id: getNewId(section.id),
			fields: section.fields.map((field) => ({
				...field,
				id: getNewId(field.id),
				odontogramFields: field.odontogramFields?.map((subField) => ({
					...subField,
					id: getNewId(subField.id),
				})),
				appointmentFields: field.appointmentFields?.map((subField) => ({
					...subField,
					id: getNewId(subField.id),
				})),
			})),
		})),
	};

	const clonedRules: FormRule[] = template.rules.map((rule) => ({
		...rule,
		id: uuidv4(),
		conditionGroup: {
			...rule.conditionGroup,
			conditions: rule.conditionGroup.conditions.map((cond) => ({
				...cond,
				fieldId: getNewId(cond.fieldId),
			})),
		},
		action: cloneAction(rule.action, getNewId),
	}));

	return {
		title: template.title,
		description: template.description ?? "",
		bg_color: template.bg_color,
		organization_id: organizationId,
		definition: clonedDefinition,
		rules: clonedRules,
	};
}

function cloneAction(
	action: FormRule["action"],
	getNewId: (oldId: string) => string
): FormRule["action"] {
	switch (action.type) {
		case "create_patient":
			return {
				...action,
				fieldMappings: action.fieldMappings.map((m) => ({
					...m,
					formFieldId: m.formFieldId
						? getNewId(m.formFieldId)
						: undefined,
				})),
			};
		case "prefill_job_fields":
			return {
				...action,
				fieldMappings: action.fieldMappings.map((m) => ({
					...m,
					formFieldId: m.formFieldId
						? getNewId(m.formFieldId)
						: undefined,
				})),
			};
		case "create_job_document":
			return { ...action };
	}
}
